import { useEffect, useState, useCallback } from "react";
import api, { API, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Layout } from "@/components/Layout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { CheckCircle, XCircle, NotePencil, Stack, ArrowRight, WarningCircle } from "@phosphor-icons/react";
import { RejectDialog } from "@/components/RejectDialog";
import { toast } from "sonner";
import { memberTypeLabel } from "@/lib/constants";

const FIELD_LABELS = {
  title: "Title", first_name: "First name", middle_name: "Middle name", last_name: "Last name",
  date_of_birth: "Date of birth", sex: "Sex", contact_number: "Contact number",
  alternate_contact_number: "Alternate contact", email: "Email", next_of_kin_name: "Next of kin",
  next_of_kin_contact: "Next of kin contact", next_of_kin_relationship: "Next of kin relationship",
  professional_grade: "Professional Grade", rank: "Cadre", facility_name: "Facility",
  facility_type: "Facility type", town: "Town", district: "District", region_name: "Region",
  education: "Education", profile_picture: "Profile photo", pays_dues: "Dues payment", dues_mode: "Payment mode", tshirt_size: "T-Shirt size",
  ghana_card_number: "Ghana Card", marital_status: "Marital status", spouse_name: "Spouse name",
  number_of_children: "No. of children", biological_children_names: "Children's names", children: "Biological children", no_children: "No biological children",
  mother_name: "Mother's name", mother_status: "Mother's status", father_name: "Father's name", father_status: "Father's status",
  emergency_contact_name: "Emergency contact", emergency_contact_phone: "Emergency phone",
  emergency_contact_email: "Emergency email", emergency_contact_address: "Emergency address",
  worked_at_previous_facility: "Worked previously", previous_facility_name: "Previous facility",
  previous_facility_location: "Previous location", previous_facility_type: "Previous facility type",
  member_type: "Type of membership", custom_fields: "Additional information",
};

const fmt = (v) => {
  if (Array.isArray(v)) {
    if (v.length && "full_name" in v[0]) return v.map((c) => `${c.full_name || "—"} (${c.sex || "—"}, ${c.date_of_birth || "—"})`).join("; ");
    return v.map((e) => `${e.institution || "—"} (${e.qualification || "—"}, ${e.entry_year || "?"}-${e.graduation_year || "?"})`).join("; ") || "—";
  }
  return v === "" || v == null ? "—" : String(v);
};

const TABS = [
  { key: "pending_review", label: "Awaiting National Review" },
  { key: "approved", label: "Approved" },
  { key: "rejected", label: "Rejected" },
];

let labelsPromise;
function CustomChange({ value, testId }) {
  const [labels, setLabels] = useState({});
  useEffect(() => {
    labelsPromise = labelsPromise || api.get("/forms/labels").then(({ data }) => data).catch(() => ({}));
    labelsPromise.then(setLabels);
  }, []);
  const rows = Object.entries(value || {}).filter(([, v]) => v !== "" && !(Array.isArray(v) && !v.length));
  return <span data-testid={testId} className="break-words min-w-0">{rows.length ? rows.map(([k, v]) => `${labels[k]?.label || k}: ${labels[k]?.type === "file" ? "file uploaded" : Array.isArray(v) ? v.join(", ") : v}`).join("; ") : "—"}</span>;
}

function ChangeValue({ field, value, testId }) {
  if (field === "custom_fields") return <CustomChange value={value} testId={testId} />;
  if (field === "profile_picture" && value) {
    const src = /^(https?:|data:)/.test(value) ? value : `${API}/files/${value}?auth=${localStorage.getItem("token")}`;
    return <img src={src} alt="Profile photo" data-testid={testId} className="h-20 w-20 rounded-md object-cover" />;
  }
  return <span data-testid={testId} className="break-words min-w-0">{field === "member_type" ? memberTypeLabel(value) : fmt(value)}</span>;
}

export default function EditRequests() {
  const { user } = useAuth();
  const [tab, setTab] = useState("pending_review");
  const [items, setItems] = useState([]);
  const [busyId, setBusyId] = useState(null);
  const [rejectTarget, setRejectTarget] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const { data } = await api.get("/edit-requests", { params: { status: tab } });
      setItems(data);
    } catch (err) { setError(formatApiError(err.response?.data?.detail)); }
    finally { setLoading(false); }
  }, [tab]);

  useEffect(() => { load(); }, [load]);

  const act = async (request, path, msg, body = {}) => {
    setBusyId(request.id);
    try {
      await api.post(`/edit-requests/${request.id}/${path}`, { ...body, revision: request.revision });
      toast.success(msg);
      setRejectTarget(null);
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      if (err.response?.status === 409) await load();
    } finally {
      setBusyId(null);
    }
  };

  return (
    <Layout>
      <div className="animate-fade-up max-w-4xl">
        <span className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Workflow</span>
        <h1 data-testid="profile-approvals-heading" className="font-display text-3xl sm:text-4xl font-bold tracking-tight mt-1">Profile Staging &amp; Approvals</h1>
        <p className="text-sm text-muted-foreground mt-2">
          Review member-submitted changes. Only National-level approval publishes them; rejection keeps the current profile unchanged.
        </p>
        <Button variant="outline" size="sm" className="mt-3" onClick={load} disabled={loading} data-testid="refresh-profile-approvals">Refresh queue</Button>

        <Tabs value={tab} onValueChange={setTab} className="mt-6">
          <TabsList>
            {TABS.map((t) => (
              <TabsTrigger key={t.key} value={t.key} data-testid={`tab-${t.key}`}>{t.label}</TabsTrigger>
            ))}
          </TabsList>
        </Tabs>

        <div className="mt-6 space-y-3">
          {loading && <p data-testid="profile-queue-loading" role="status">Loading profile updates…</p>}
          {error && <p data-testid="profile-queue-error" role="alert" className="text-destructive">{error}</p>}
          {!loading && !error && items.length === 0 && (
            <Card data-testid="profile-queue-empty" className="border-dashed border-border p-10 text-center">
              <Stack size={28} className="mx-auto text-muted-foreground" />
              <p className="mt-3 text-sm text-muted-foreground">Nothing in this queue right now.</p>
            </Card>
          )}
          {!loading && !error && items.map((r) => (
            <Card key={r.id} data-testid={`edit-request-${r.member_staff_id}`} className="p-5 border-border">
              <div className="flex flex-col sm:flex-row sm:items-start gap-4 justify-between">
                <div className="min-w-0">
                  <p className="font-semibold">{r.member_name}</p>
                  <p className="text-xs text-muted-foreground font-mono mb-2">{r.member_staff_id}</p>
                  <p className="text-sm" data-testid={`profile-review-meta-${r.id}`}>Revision {r.revision || 1} · {new Date(r.submitted_at || r.requested_at).toLocaleString()}</p>
                  {r.review_note && <p className="text-sm text-destructive mt-1" data-testid={`profile-review-note-${r.id}`}>{r.review_note}</p>}
                  {r.reviewed_by_name && <p className="text-xs text-muted-foreground mt-1" data-testid={`profile-reviewed-by-${r.id}`}>Reviewed by {r.reviewed_by_name}</p>}
                  {r.member_id === user.id && tab === "pending_review" && <p className="text-xs text-muted-foreground mt-1" data-testid="own-profile-review-note">Another National-level manager must review your changes.</p>}
                </div>
                <div className="flex flex-wrap gap-2 shrink-0">
                  {tab === "pending_review" && r.member_id !== user.id && (
                    <>
                      <Button size="sm" disabled={!!busyId}
                        onClick={() => act(r, "approve", "Changes approved and published")}
                        data-testid={`approve-edit-${r.member_staff_id}`} className="gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white">
                        <CheckCircle size={16} weight="bold" /> Approve &amp; Publish
                      </Button>
                      <Button size="sm" variant="outline" disabled={!!busyId}
                        onClick={() => setRejectTarget(r)}
                        data-testid={`reject-edit-${r.member_staff_id}`}
                        className="gap-1.5 border-destructive text-destructive hover:bg-destructive hover:text-destructive-foreground">
                        <XCircle size={16} weight="bold" /> Reject
                      </Button>
                    </>
                  )}
                </div>
              </div>

              {r.facility_check && (
                <div role="note" data-testid={`facility-check-${r.id}`} className="mt-4 rounded-md border border-amber-400 bg-amber-50 dark:bg-amber-500/10 p-3 text-sm flex gap-2">
                  <WarningCircle size={18} weight="fill" className="text-amber-600 shrink-0 mt-0.5" />
                  <span><strong>Check facility type:</strong> the directory lists {r.facility_check.facility_name} as <strong>{r.facility_check.directory_type}</strong>, but the member chose <strong>{r.facility_check.chosen_type}</strong>.</span>
                </div>
              )}
              {r.changes && (
                <div className="mt-4 border-t border-border pt-4 space-y-2" data-testid={`changes-${r.member_staff_id}`}>
                  <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground flex items-center gap-1.5">
                    <NotePencil size={14} weight="bold" /> Proposed changes
                  </p>
                  {Object.entries(r.changes).map(([field, ch]) => (
                    <div key={field} className="text-sm grid sm:grid-cols-[9rem_minmax(0,1fr)_auto_minmax(0,1fr)] gap-2 py-2">
                      <span className="font-medium">{FIELD_LABELS[field] || field}</span>
                      <div className="min-w-0 text-muted-foreground"><span className="block text-xs mb-1">Published</span><ChangeValue field={field} value={ch.old} testId={`change-old-${r.id}-${field}`} /></div>
                      <ArrowRight size={12} className="text-muted-foreground hidden sm:inline mt-5" />
                      <div className="min-w-0 text-emerald-700 dark:text-emerald-400"><span className="block text-xs mb-1">Proposed</span><ChangeValue field={field} value={ch.new} testId={`change-new-${r.id}-${field}`} /></div>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          ))}
        </div>
      </div>

      <RejectDialog title="Reject profile changes" open={!!rejectTarget} onOpenChange={(o) => !o && setRejectTarget(null)}
        name={rejectTarget ? rejectTarget.member_name : ""}
        onConfirm={(reason) => act(rejectTarget, "reject", "Changes rejected; published profile unchanged", { note: reason })} />
    </Layout>
  );
}
